/**
 * Utilities for the US `(000) 000-0000` mask: normalize digits so we never store
 * `+11…` when the user already included a leading national `1` or full
 * `1` + 10-digit NANP.
 */

/**
 * Parses non-digit characters out of a masked input string.
 *
 * @param raw - Raw value from the mask (may include spaces, parens, dashes).
 * @returns Contiguous digits only.
 */
function digitsOnly(raw: string): string {
  return raw.replace(/\D/g, '');
}

/**
 * Converts IMask NANP input into E.164 `+1` storage form.
 * Strips a pasted full US number (`13456789101`) to 10 national digits before
 * prefixing `+1`, so we never store `+113…` from duplicate leading `1`s.
 *
 * @param rawMasked - Raw string from the IMask `onAccept` callback.
 * @returns Stored value such as `+13456789101`, or empty string when there are no digits.
 */
export function maskedNanpInputToE164(rawMasked: string): string {
  let nsn = digitsOnly(rawMasked);
  if (nsn.length === 0) {
    return '';
  }
  // Drop extra leading `1`s when the mask yields more than 10 digits (e.g. `11234567890`
  // from mixed formatted + country-code confusion) before taking the last 10.
  while (nsn.length > 10 && nsn.startsWith('1')) {
    nsn = nsn.slice(1);
  }
  // Single full US number with country digit: 1 + NXX-NXX-XXXX
  if (nsn.length > 10) {
    nsn = nsn.slice(-10);
  }
  return `+1${nsn}`;
}

/**
 * Canonical `+1` E.164 for outbound API payloads. Use when the value may never have
 * passed through IMask `onAccept` (e.g. prefilled display text like `+1 (612) 442-0633`,
 * copy/paste, or hydration). Delegates to {@link maskedNanpInputToE164}.
 *
 * @param value - Raw field string from the form.
 * @returns E.164 string or `undefined` when empty.
 */
export function normalizeUsPhoneForApi(
  value: string | undefined | null
): string | undefined {
  if (value == null || value === '') {
    return undefined;
  }
  const e164 = maskedNanpInputToE164(String(value));
  return e164 === '' ? undefined : e164;
}

/**
 * Extracts national digits for the `(000) 000-0000` mask from the stored value.
 *
 * Stored values use the digit shape `1` + national (e.g. `+13` → digits `13` → national
 * `3` only). Treating `13` as two national digits made the mask show `(13_)…` and fill
 * with spurious `1`s; the first `1` after `+` is always US country code here.
 *
 * @param stored - Form state such as `+13456789101` or partial `+1345`.
 * @returns National digits only (no country code) for the mask, max 10 characters.
 */
export function e164ToNanpMaskDigits(stored: string): string {
  const d = digitsOnly(stored);
  if (d.length === 0) {
    return '';
  }
  // `+1` + nationalDigits → digit string starts with `1` (country); rest is national.
  const national = d.startsWith('1') ? d.slice(1) : d;
  if (national.length > 10) {
    return national.slice(-10);
  }
  return national;
}
